import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useGetMany, useNotify, useRedirect, useRefresh } from "react-admin";
import {
  Alert,
  AlertTitle,
  Box,
  Button as MuiButton,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
// House Button (loading + icon treatment) for ACTIONS, per the ux-coronis
// standard and the DeleteImpactDialog precedent. The option cards below stay
// raw MUI Buttons: they are selection toggles with custom layout, not actions.
import Button from "@/components/ui/Button";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";
import type { Claimant, Vendor } from "@/types";
import { formatYearRange } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { ResourceName } from "@/types/common";
import type { DuplicateClaimantPair } from "@/types/duplicateClaimantPair";
import {
  MERGE_BLOCK_LABELS,
  MERGE_CONFLICT_FIELD_LABELS,
  MERGE_PROJECTION_UNAVAILABLE_LABELS,
  type ClaimantMergePreview,
} from "@/types/claimantMerge";
import { formatAmount } from "./DuplicateClaimantPairListCells";

/**
 * Claimant merge (#7064), rendered inside the review dialog's frame for a
 * CONFIRMED pair, to operators holding claimant-merges:create -- granted to
 * every pair-triage role by 000232 (ReadOnly excluded). It swaps into the
 * review dialog rather than stacking a second modal on it (#7224, house rule:
 * dialogs never stack); `MergeClaimantPairDialog` below wraps the same step in
 * its own Dialog for standalone use and tests.
 *
 * Two screens, one decision each (#7224 -- one screen carrying the pick, the
 * record outcome and the money outcome was the thing operators could not scan):
 *   1. KEEP WHICH RECORD. Two cards with the facts that tell same-named records
 *      apart. Direction is the operator's decision -- the engine's suggestion
 *      arrives with the preview as a badge and a one-line reason, never a
 *      preselection (review !4800). Picking fetches the preview; Next advances.
 *   2. REVIEW AND MERGE. Only what changes: the deltas on the record as a
 *      short list, then the claims table (ADR-078's DeleteImpactDialog shape),
 *      then the irreversibility acknowledgement beside the button. A blocked
 *      preview renders its reasons here instead -- information, not an error.
 *      Where the two rows conflict in the SAME identifier space, the line
 *      names the value that is not retained and that a future file carrying
 *      it will re-create a duplicate (#7216).
 *
 * The execute endpoint re-resolves the preview server-side, so a stale preview
 * cannot merge: a 409 comes back either with a fresh blocked preview (rendered
 * in place) or as a plain conflict when the pair stopped being confirmed.
 */

const display = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
};

interface StepProps {
  pair: DuplicateClaimantPair;
  a?: Claimant;
  b?: Claimant;
  /** Back to the comparison (or close, when standalone). The pair stays confirmed. */
  onBack: () => void;
  /** Called after a successful merge, before refresh -- the parent closes itself. */
  onMerged: () => void;
  /** True while the irreversible call is in flight; the parent blocks Escape. */
  onBusyChange?: (busy: boolean) => void;
}

export const MergeClaimantPairStep = ({ pair, a, b, onBack, onMerged, onBusyChange }: StepProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();

  const [screen, setScreen] = useState<"pick" | "review">("pick");
  const [parentId, setParentId] = useState<string | null>(null);
  const [preview, setPreview] = useState<ClaimantMergePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [merging, setMerging] = useState(false);
  const [prefetchedSuggestion, setPrefetchedSuggestion] = useState<ClaimantMergePreview["suggestion"]>();

  useEffect(() => {
    onBusyChange?.(merging);
  }, [merging, onBusyChange]);

  // The suggestion only travels inside a preview, so fetch one on open with a
  // placeholder direction and keep ONLY its suggestion: nothing is selected,
  // Next stays dead, and the operator sees the advice before choosing (#7224).
  // Advice only -- a failure here is silent; the real preview fails visibly
  // on pick. The preview endpoint is a read.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await httpClient(`${getPartnerAwareApiUrl()}/claimant-merges/preview`, {
          method: "POST",
          body: JSON.stringify({ pair_id: pair.id, parent_id: pair.claimant_a_id }),
        });
        if (!cancelled) setPrefetchedSuggestion((res?.json as ClaimantMergePreview | undefined)?.suggestion);
      } catch {
        // advice only
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pair.id, pair.claimant_a_id]);

  // The operator's LATEST choice. Responses for anything else are dropped:
  // without this, A-then-B clicks resolving out of order leave B's direction
  // in state while A's preview renders -- and the operator acknowledges the
  // wrong kept/dropped list on an irreversible action (review !4800 thread
  // 29134). The cards are also disabled while a preview loads, and merge()
  // sends preview.parent_id, so the acknowledged document and the executed
  // direction are the same object by construction.
  const latestChoice = useRef<string | null>(null);

  const loadPreview = useCallback(
    async (chosenParent: string) => {
      latestChoice.current = chosenParent;
      setPreviewLoading(true);
      setPreviewError(false);
      setAcknowledged(false);
      // Clear the outgoing preview: after a re-choose, parentId has flipped but
      // the OLD direction's document would otherwise satisfy Next until the
      // new response lands.
      setPreview(null);
      try {
        const { json } = await httpClient(`${getPartnerAwareApiUrl()}/claimant-merges/preview`, {
          method: "POST",
          body: JSON.stringify({ pair_id: pair.id, parent_id: chosenParent }),
        });
        if (latestChoice.current !== chosenParent) return; // stale response
        setPreview(json as ClaimantMergePreview);
      } catch (err) {
        if (latestChoice.current !== chosenParent) return;
        // Fail VISIBLE: an absent preview must never read as "nothing blocks".
        setPreview(null);
        setPreviewError(true);
        console.error("merge preview failed", err);
      } finally {
        if (latestChoice.current === chosenParent) {
          setPreviewLoading(false);
        }
      }
    },
    [pair.id]
  );

  const choose = (id: string) => {
    // Re-picking the chosen card is a no-op: a refetch only blinks the screen.
    if (id === parentId && (preview || previewLoading)) return;
    setParentId(id);
    void loadPreview(id);
  };

  const merge = async () => {
    // Execute the direction the operator ACKNOWLEDGED -- the preview's, never
    // the click state (thread 29134).
    if (!preview?.allowed) return;
    setMerging(true);
    try {
      await httpClient(`${getPartnerAwareApiUrl()}/claimant-merges`, {
        method: "POST",
        body: JSON.stringify({ pair_id: pair.id, parent_id: preview.parent_id }),
      });
      notify("Records merged — totals are recomputing", { type: "success" });
      onMerged();
      refresh();
      // Land the operator on the record they kept (review !4807): the pair row
      // leaves the queue, and the survivor is where the merged claims and the
      // recompute result now live. The PREVIEWED parent, same rule as execute.
      redirect("show", ResourceName.CLAIMANTS, preview.parent_id);
    } catch (err: unknown) {
      // 409 with a preview body = the server re-resolved and refused; render the
      // fresh preview in place. Any other failure is a plain error.
      //
      // WHERE THE BODY ACTUALLY LIVES: httpClient normalizes every rejection via
      // ra-commons into an AegleApiError, and the untouched backend JSON survives
      // only at `.body.raw.body` (the original HttpError under `raw`). The naive
      // `err.body` read can never see the preview -- verified against the live
      // endpoint 2026-09-29, after a unit test mocking the convenient shape
      // passed against extraction the real client made unreachable (the same
      // hollow-fixture failure as !4646's total=40).
      const norm = (err as { body?: { raw?: { body?: unknown } } })?.body;
      const candidate = norm?.raw?.body ?? (norm as unknown);
      if (candidate && typeof candidate === "object" && "allowed" in candidate) {
        setPreview(candidate as ClaimantMergePreview);
        setAcknowledged(false);
        notify("The merge was refused — the state changed since the preview", { type: "warning" });
      } else {
        // No preview in the body: the pair left 'confirmed' (or a plain
        // failure). Surface the API's own message where it has one, and clear
        // the stale allowed preview + acknowledgement so an out-of-date "ready
        // to merge" view does not stay on screen (thread 29138).
        const message = (norm as { message?: string } | undefined)?.message || "Merge failed — nothing was changed";
        setPreview(null);
        setAcknowledged(false);
        setScreen("pick");
        notify(message, { type: "error" });
        console.error("merge failed", err);
      }
    } finally {
      setMerging(false);
    }
  };

  const byId = (id: string | undefined) => (a && String(a.id) === id ? a : b && String(b.id) === id ? b : undefined);

  const nameOf = (id: string | undefined): string => {
    const c = byId(id);
    return c ? display(c.claimant_name) : (id ?? "—");
  };

  // The names are identical by construction (that is why the pair exists), so
  // every sentence naming a side carries the Claimant A/B label the cards and
  // the review grid already use (thread 29135).
  const labelFor = (id: string | undefined): string => (id === pair.claimant_a_id ? "Claimant A" : "Claimant B");

  // Everything that tells two same-named records apart, per card: the pair's
  // recorded paid amounts plus each row's identifiers (local review feedback
  // 2026-09-29 -- two "Yvonne Williams" cards with no other content are a coin
  // flip, and the coin flip is the one decision this screen exists to make).
  const cardFacts = (id: string) => {
    const claimant = byId(id);
    const isA = id === pair.claimant_a_id;
    return {
      label: isA ? "Claimant A" : "Claimant B",
      name: claimant ? display(claimant.claimant_name) : id,
      paid: formatAmount(isA ? pair.claimant_a_paid_amount : pair.claimant_b_paid_amount),
      dob: display(claimant?.claimant_birth_date),
      medicalId: display(claimant?.member_id),
      rxId: display(claimant?.rx_member_id),
    };
  };

  // From the PREVIEW, not the click state: everything on the review screen
  // describes the document being acknowledged (same rule as merge() executing
  // preview.parent_id).
  const absorbedId = preview?.absorbed_id;
  const suggestion = preview?.suggestion ?? prefetchedSuggestion;
  const blocked = preview?.blocked ?? [];
  const conflicts = preview?.conflicts ?? [];
  const identifiers = preview?.identifiers ?? [];
  const slcs = preview?.slcs ?? [];
  const projections = preview?.projections ?? [];
  const notePolicies = preview?.combined_note_policy_ids?.length ?? 0;

  // The deltas on the record, one line each: gained identifiers, discarded
  // field values, and identifiers NOT retained (both rows carry one -- the
  // merge that can come back, #7216). Values come off the rows of the
  // PREVIEW's direction.
  const spaceLabel = (space: "medical" | "rx") => (space === "rx" ? "Rx ID" : "Medical ID");
  const spaceKey = (space: "medical" | "rx"): keyof Claimant => (space === "rx" ? "rx_member_id" : "member_id");
  const absorbedRow = byId(preview?.absorbed_id);
  const absLabel = labelFor(preview?.absorbed_id);
  const gained = identifiers.filter((i) => i.resolved === "absorbed" && i.absorbed_value_retained);
  const droppedIdentifiers = identifiers
    .filter((i) => !i.absorbed_value_retained)
    .map((i) => ({ space: i.space, label: spaceLabel(i.space), value: display(absorbedRow?.[spaceKey(i.space)]) }));

  // Policy headings follow the app convention -- carrier company name + plan-year
  // range, never the aegle_id (review !4806 thread 29260) -- with the same
  // helpers and "No Carrier" fallback as PolicySelector.
  const carrierIds = [...new Set(projections.map((pj) => pj.carrier_vendor_id).filter((id): id is string => !!id))];
  const { data: carriers = [] } = useGetMany<Vendor>(
    ResourceName.VENDORS,
    { ids: carrierIds },
    { enabled: carrierIds.length > 0 }
  );
  const policyHeading = (pj: (typeof projections)[number]) => {
    const carrier = pj.carrier_vendor_id
      ? (carriers.find((v) => String(v.id) === pj.carrier_vendor_id)?.company_name ?? "")
      : "No Carrier";
    const years = formatYearRange(pj.effective_start_date, pj.effective_end_date);
    return [carrier, years].filter(Boolean).join(" · ") || "Policy";
  };

  // The After column is the outcome; a heavier left rule separates it from the
  // inputs so the table reads "now | after".
  const afterCellSx = { borderLeft: 2, borderLeftColor: "divider", pl: 2 } as const;

  // Every claim row states its OWN outcome in plain words, with the server's
  // projected figure. One statement per claim -- the old layout narrated the
  // same outcome in three places and they contradicted each other.
  const claimRow = (s: (typeof slcs)[number], pj?: (typeof projections)[number]) => {
    const onAbsorbed = s.claimant_id === preview?.absorbed_id;
    const who = onAbsorbed ? labelFor(s.claimant_id) : "";
    let after: string;
    let note: string;
    if (s.action === "frozen") {
      // #7244: a frozen claim MOVES with the merge, amounts and status intact.
      // "already submitted" only when the status says so: a frozen claim can
      // still read 'new' (held behind a submitted one), and the old copy
      // contradicted the Status column beside it (#7224).
      after = formatAmount(String(s.total_paid_amount));
      const why = s.claim_status === "new" ? "" : " — already submitted";
      note = (onAbsorbed ? "moves to survivor, unchanged" : "unchanged") + why;
    } else if (s.action === "void_regenerate") {
      after = "—";
      note = "combined into the survivor's claim";
    } else if (pj && !pj.unavailable && pj.open_claim_id === s.slc_id) {
      after = formatAmount(String(pj.open_claim_after ?? 0));
      note = onAbsorbed ? "moves to survivor, updated" : "updated";
    } else {
      after = "—";
      note = onAbsorbed ? "moves to survivor; updates at recompute" : "updates at recompute";
    }
    return (
      <TableRow key={s.slc_id}>
        <TableCell>
          {/* Operators do not know claims by aegle id (#7224): type, status and
              amounts identify the row, and the link opens the claim itself. */}
          <Link to={`/${ResourceName.STOPLOSS_CLAIMS}/${s.slc_id}/show`} data-testid={`merge-claim-link-${s.slc_id}`}>
            {formatToTitleCase(s.claim_type)} claim
          </Link>
          {who && (
            <Typography variant="caption" color="text.secondary" display="block">
              on {who}
            </Typography>
          )}
        </TableCell>
        {/* A frozen claim shows its real status (submitted, reimbursed, denied…) --
            not a blanket "Submitted" (review !4807 thread 29263). */}
        <TableCell>{s.action === "frozen" ? formatToTitleCase(s.claim_status) : "Open"}</TableCell>
        <TableCell align="right">{formatAmount(String(s.total_paid_amount))}</TableCell>
        <TableCell align="right" sx={afterCellSx}>
          {after}
          <Typography variant="caption" color="text.secondary" display="block">
            {note}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  const claimTable = (label: string, rows: typeof slcs, pj?: (typeof projections)[number]) => {
    const newClaim = pj && !pj.unavailable && !pj.open_claim_id && (pj.open_claim_after ?? 0) > 0;
    const delta = pj?.claimed_after !== undefined ? pj.claimed_after - pj.claimed_before : undefined;
    return (
      <Box key={label} data-testid="merge-claims-table">
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Claims
          {pj && (
            <>
              {" · "}
              <strong>{policyHeading(pj)}</strong> · Paid {formatAmount(String(pj.combined_paid))}
              {pj.specific_deductible !== undefined && pj.specific_deductible !== null
                ? ` · ${formatAmount(String(pj.specific_deductible))} deductible`
                : ""}
            </>
          )}
        </Typography>
        {pj?.unavailable && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {MERGE_PROJECTION_UNAVAILABLE_LABELS[pj.unavailable] ?? "Amounts update when totals recompute"}
          </Typography>
        )}
        <Table size="small" aria-label={`Stop-loss claims — ${label}`}>
          <TableHead>
            <TableRow>
              <TableCell>Claim</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Now</TableCell>
              <TableCell align="right" sx={afterCellSx}>
                After
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((s) => claimRow(s, pj))}
            {newClaim && (
              <TableRow data-testid="merge-new-claim-row">
                <TableCell>New claim</TableCell>
                <TableCell>Open</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right" sx={afterCellSx}>
                  {formatAmount(String(pj.open_claim_after))}
                  <Typography variant="caption" color="text.secondary" display="block">
                    created at recompute
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {pj && (
              <TableRow data-testid="merge-claims-total">
                <TableCell colSpan={2}>
                  <strong>Total claimed</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{formatAmount(String(pj.claimed_before))}</strong>
                </TableCell>
                <TableCell align="right" sx={afterCellSx}>
                  <strong>{pj.claimed_after !== undefined ? formatAmount(String(pj.claimed_after)) : "—"}</strong>
                  {delta !== undefined && delta > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      +{formatAmount(String(delta))}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    );
  };

  // Group the claims by projected policy; anything not covered by a projection
  // still renders, without figures, rather than silently disappearing.
  const coveredPolicies = new Set(projections.map((pj) => pj.policy_id));
  const uncovered = slcs.filter((s) => !s.policy_id || !coveredPolicies.has(s.policy_id));

  const title = (
    <DialogTitle variant="h3" sx={{ pb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <CallMergeIcon color="primary" />
        <span>Merge {nameOf(pair.claimant_a_id)}</span>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="merge-step-caption">
        {screen === "pick" ? "Step 1 of 2 · Keep which record?" : "Step 2 of 2 · Review and merge"}
      </Typography>
    </DialogTitle>
  );

  // ---- Screen 1: keep which record -------------------------------------------
  if (screen === "pick") {
    return (
      <>
        {title}
        <DialogContent dividers>
          <Stack direction="row" spacing={1.5}>
            {[pair.claimant_a_id, pair.claimant_b_id].map((id) => {
              const facts = cardFacts(id);
              const chosen = parentId === id;
              const suggested = suggestion?.claimant_id === id;
              return (
                <MuiButton
                  key={id}
                  variant="outlined"
                  color={chosen ? "primary" : "inherit"}
                  onClick={() => choose(id)}
                  disabled={merging || previewLoading}
                  // U2 (ux-coronis a11y): the cards are toggles, and without this
                  // the selection state exists only in the border colour --
                  // invisible to assistive tech.
                  aria-pressed={chosen}
                  data-testid={`choose-parent-${id}`}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    textAlign: "left",
                    alignItems: "stretch",
                    px: 1.5,
                    py: 1.25,
                    borderWidth: chosen ? 2 : 1,
                    borderColor: chosen ? "primary.main" : "divider",
                    "&:hover": { borderWidth: chosen ? 2 : 1 },
                    color: "text.primary",
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {chosen ? (
                        <CheckCircleIcon color="primary" fontSize="small" />
                      ) : (
                        <RadioButtonUncheckedIcon fontSize="small" sx={{ color: "text.disabled" }} />
                      )}
                      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                        {facts.label}
                      </Typography>
                      {suggested && (
                        <Chip
                          size="small"
                          label="Suggested"
                          color="info"
                          variant="outlined"
                          data-testid="merge-suggested-badge"
                        />
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Paid <strong>{facts.paid}</strong> · DOB {facts.dob}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Medical ID {facts.medicalId} · Rx ID {facts.rxId}
                    </Typography>
                  </Box>
                </MuiButton>
              );
            })}
          </Stack>
          {/* The reason in one visible sentence (#6519 UX AC): the badge marks
              the card, this line says why. Advice only -- nothing is preselected. */}
          {suggestion && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1.5 }}
              data-testid="merge-suggestion"
            >
              Suggested: {labelFor(suggestion.claimant_id)} — {suggestion.reason}
            </Typography>
          )}
          {previewError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              The impact preview could not be loaded. Nothing can merge without it.
            </Alert>
          )}
        </DialogContent>
        {/* Cancel returns to the comparison: the pair stays confirmed and
            Merge… stays offered (#7478). Next needs a preview -- allowed OR
            blocked, since a block is information the operator should see. */}
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button variant="text" color="primary" onClick={onBack} disabled={merging}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            loading={previewLoading}
            onClick={() => setScreen("review")}
            disabled={!preview || previewLoading}
            data-testid="merge-next"
          >
            Next
          </Button>
        </DialogActions>
      </>
    );
  }

  // ---- Screen 2: review and merge --------------------------------------------
  const parentLabel = labelFor(preview?.parent_id);
  return (
    <>
      {title}
      <DialogContent dividers>
        {preview && !preview.allowed ? (
          <Alert severity="warning" data-testid="merge-blocked">
            <AlertTitle>This pair can't merge yet</AlertTitle>
            {blocked.map((e, i) => (
              <Typography key={i} variant="body2">
                {/* Label only. `detail` is a developer note (ticket refs,
                    internal rule names) for logs and the ledger -- operators
                    get the reason-code copy (thread 29168). */}
                • {MERGE_BLOCK_LABELS[e.reason] ?? e.reason}
              </Typography>
            ))}
          </Alert>
        ) : (
          preview && (
            <Box data-testid="merge-preview">
              <Typography variant="body2" sx={{ mb: 1 }}>
                Keeping <strong>{parentLabel}</strong> — {nameOf(preview.parent_id)}. {absLabel} is retired; its claims
                move onto {parentLabel}.
              </Typography>

              {/* What changes on the record, one line each. */}
              <Box
                component="ul"
                sx={{ m: 0, mb: 2.5, pl: 2.5, "& li": { py: 0.25 } }}
                data-testid="merge-record-deltas"
              >
                {gained.map((i) => (
                  <Typography component="li" variant="body2" key={`gain-${i.space}`} data-testid={`gain-${i.space}`}>
                    Gains {spaceLabel(i.space)} <strong>{display(absorbedRow?.[spaceKey(i.space)])}</strong> from{" "}
                    {absLabel}
                  </Typography>
                ))}
                {conflicts.map((c) => (
                  <Typography component="li" variant="body2" key={c.field} data-testid={`drop-${c.field}`}>
                    Drops {absLabel}'s {(MERGE_CONFLICT_FIELD_LABELS[c.field] ?? c.field).toLowerCase()}{" "}
                    <strong>{display(c.absorbed_value)}</strong> — keeps {display(c.parent_value)}
                  </Typography>
                ))}
                {droppedIdentifiers.map((d) => (
                  <Typography
                    component="li"
                    variant="body2"
                    key={`dropid-${d.space}`}
                    sx={{ color: "warning.dark" }}
                    data-testid="same-space-warning"
                  >
                    <WarningAmberIcon sx={{ fontSize: 16, verticalAlign: "-3px", mr: 0.5 }} />
                    Drops {absLabel}'s {d.label} <strong>{d.value}</strong> — a future file sent under it will re-create
                    a duplicate for review
                  </Typography>
                ))}
                {notePolicies > 0 && (
                  <Typography component="li" variant="body2" data-testid="note-combine-callout">
                    Combines the Claims Summary notes from both records
                    {notePolicies > 1 ? ` (${notePolicies} policies)` : ""}
                  </Typography>
                )}
                {gained.length === 0 &&
                  conflicts.length === 0 &&
                  droppedIdentifiers.length === 0 &&
                  notePolicies === 0 && (
                    <Typography
                      component="li"
                      variant="body2"
                      color="text.secondary"
                      data-testid="merge-no-record-changes"
                    >
                      Keeps all of {parentLabel}'s details; nothing from {absLabel} is carried over
                    </Typography>
                  )}
              </Box>

              <Stack spacing={2}>
                {projections.map((pj) =>
                  claimTable(
                    pj.policy_id,
                    slcs.filter((s) => s.policy_id === pj.policy_id),
                    pj
                  )
                )}
                {uncovered.length > 0 && claimTable("other", uncovered)}
              </Stack>
              {/* An empty section would read as "the preview found nothing",
                  which is not the same as "nothing moves". Say it. */}
              {slcs.length === 0 && projections.length === 0 && (
                <Typography variant="body2" color="text.secondary" data-testid="merge-nothing-moves">
                  No claims move — only the retired record changes.
                </Typography>
              )}
            </Box>
          )
        )}
      </DialogContent>

      {/* Confirm. The acknowledgement sits next to Merge so a disabled button
          is never unexplained. Back returns to the pick. */}
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="text" color="primary" onClick={() => setScreen("pick")} disabled={merging}>
          Back
        </Button>
        {preview?.allowed && (
          <FormControlLabel
            sx={{ flex: 1, ml: 1, mr: 1 }}
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                data-testid="merge-acknowledge"
              />
            }
            label={
              <Typography variant="body2">Retire {labelFor(absorbedId)} — I understand this can't be undone</Typography>
            }
          />
        )}
        <Box sx={{ flex: preview?.allowed ? 0 : 1 }} />
        <Button
          variant="contained"
          color="warning"
          startIcon={<CallMergeIcon />}
          loading={merging}
          onClick={merge}
          disabled={!preview?.allowed || !acknowledged}
          data-testid="merge-execute"
        >
          Merge records
        </Button>
      </DialogActions>
    </>
  );
};

interface DialogProps {
  pair: DuplicateClaimantPair;
  a?: Claimant;
  b?: Claimant;
  open: boolean;
  onClose: () => void;
  onMerged: () => void;
}

/**
 * Standalone wrapper: the same step in its own Dialog. The review dialog
 * renders the step inline instead; this exists for direct use and tests.
 * Closing unmounts the step, which is what resets its state per open.
 */
export const MergeClaimantPairDialog = ({ pair, a, b, open, onClose, onMerged }: DialogProps) => {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      {open && (
        <MergeClaimantPairStep
          key={pair.id}
          pair={pair}
          a={a}
          b={b}
          onBack={onClose}
          onMerged={onMerged}
          onBusyChange={setBusy}
        />
      )}
    </Dialog>
  );
};
